import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import TenantAccessGuard from './components/TenantAccessGuard';
import Login from './pages/Login';
import Register from './pages/Register';
import TenantRegisterPage from './pages/TenantRegisterPage';
import Layout from './components/Layout';
import AdminShell from './features/super-admin/components/AdminShell';
import DashboardWrapper from './pages/DashboardWrapper';

import VSLA from './pages/VSLA';
import WarehouseView from './pages/WarehouseView';
import FarmersPage from './pages/FarmersPage';
import FarmerProfilePage from './pages/FarmerProfilePage';
// InputsSalesPage removed — replaced by marketplace supplier portal
import CohortsPage from './pages/CohortsPage';
import CompostPage from './pages/CompostPage';
import TrainingPage from './pages/TrainingPage';
import GuidancePage from './pages/GuidancePage';
import IssueManagementPage from './pages/IssueManagement';
import MapsPage from './pages/MapsPage';
import MoneyPage from './pages/MoneyPage';
import OwnerWorkPage from './pages/OwnerWorkPage';
import AnalyticsPage from './pages/AnalyticsPage';
import OrdersPage from './pages/OrdersPage';
import UsersPage from './pages/UsersPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import CropsPage from './pages/CropsPage';
import WebsiteControlPage from './pages/WebsiteControl';
import TraceabilityPage from './pages/TraceabilityPage';
import TreeInspectionsPage from './pages/TreeInspectionsPage';
import ActivitiesPage from './pages/ActivitiesPage';
import MorningRoundsPage from './pages/MorningRoundsPage';
import FieldIntelligencePage from './pages/FieldIntelligencePage';
import FieldDiaryPage from './pages/FieldDiaryPage';
import OrganisationManagementPage from './pages/OrganisationManagementPage';
import AuditLogsPage from './pages/AuditLogsPage';
import DonationsPage from './pages/DonationsPage';
import FundingReadinessPage from './pages/FundingReadinessPage';
import ManageSubscriptionsPage from './pages/ManageSubscriptionsPage';
import BlogPage from './pages/BlogPage';

// New Super Admin shell pages
import PlatformOverviewPage from './pages/PlatformOverviewPage';
import PlatformUsersPage from './pages/PlatformUsersPage';
import PlatformSettingsPage from './pages/PlatformSettingsPage';


// B2B Marketplace
import MarketplaceShell from './features/marketplace/components/MarketplaceShell';
import MarketplaceRouteGuard from './features/marketplace/components/MarketplaceRouteGuard';
import BuyerOverview from './features/marketplace/buyer/BuyerOverview';
import CatalogueBrowser from './features/marketplace/buyer/CatalogueBrowser';
import SupplierOverview from './features/marketplace/supplier/SupplierOverview';
import SupplierProfilePage from './features/marketplace/supplier/SupplierProfilePage';
import MarketplaceOverviewPage from './features/marketplace/admin/MarketplaceOverviewPage';
import MarketplaceSuppliersPage from './features/marketplace/admin/MarketplaceSuppliersPage';
import MarketplaceSettingsPage from './features/marketplace/admin/MarketplaceSettingsPage';
import MarketplaceVerificationPage from './features/marketplace/admin/MarketplaceVerificationPage';
import MarketplaceOrdersPage from './features/marketplace/admin/MarketplaceOrdersPage';
import MarketplaceFinancePage from './features/marketplace/admin/MarketplaceFinancePage';
import RfqBuilder from './features/marketplace/buyer/RfqBuilder';
import OrderDetail from './features/marketplace/buyer/OrderDetail';
import ListingEditor from './features/marketplace/supplier/ListingEditor';
import SupplierListingDetail from './features/marketplace/supplier/SupplierListingDetail';
import RfqList from './features/marketplace/buyer/RfqList';
import BuyerDeliveries from './features/marketplace/buyer/BuyerDeliveries';
import BuyerInvoices from './features/marketplace/buyer/BuyerInvoices';
import BuyerSuppliers from './features/marketplace/buyer/BuyerSuppliers';
import SupplierRfqs from './features/marketplace/supplier/SupplierRfqs';
import SupplierQuotations from './features/marketplace/supplier/SupplierQuotations';
import SupplierFulfilment from './features/marketplace/supplier/SupplierFulfilment';
import SupplierInventory from './features/marketplace/supplier/SupplierInventory';
import SupplierSettlements from './features/marketplace/supplier/SupplierSettlements';
import BuyerOrders from './features/marketplace/buyer/BuyerOrders';
import BuyerQuotations from './features/marketplace/buyer/BuyerQuotations';
import RfqDetail from './features/marketplace/buyer/RfqDetail';
import QuotationDetail from './features/marketplace/buyer/QuotationDetail';
import BuyerProfile from './features/marketplace/buyer/BuyerProfile';
import BuyerOrganisation from './features/marketplace/buyer/BuyerOrganisation';
import SupplierListings from './features/marketplace/supplier/SupplierListings';
import SupplierOrders from './features/marketplace/supplier/SupplierOrders';
import SupplierOrderDetail from './features/marketplace/supplier/SupplierOrderDetail';
import './App.css';

/**
 * Role-Based Access Control (RBAC) Configuration:
 * - super_admin: Platform owner console
 * - project_manager: Tenant/organisation workspace owner
 * - agronomist: Technical field and crop workflows
 * - field_facilitator: Last-mile field workflows
 * - farmer: Mobile-first farmer access
 */

const superAdminOnly = ['super_admin'];

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/tenant-register/:token" element={<TenantRegisterPage />} />

          {/* Redirect root to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* ────────────────────────────────────────────────────────────────── */}
          {/* Super Admin platform console — dedicated shell                    */}
          {/* Uses AdminShell (dark sidebar, white header, operational footer)  */}
          {/* ────────────────────────────────────────────────────────────────── */}
          <Route
            path="/dashboard/super-admin"
            element={
              <ProtectedRoute allowedRoles={superAdminOnly}>
                <AdminShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<PlatformOverviewPage />} />
          </Route>

          <Route
            path="/dashboard/company-management"
            element={
              <ProtectedRoute allowedRoles={superAdminOnly}>
                <AdminShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<OrganisationManagementPage />} />
          </Route>

          <Route
            path="/dashboard/platform-users"
            element={
              <ProtectedRoute allowedRoles={superAdminOnly}>
                <AdminShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<PlatformUsersPage />} />
          </Route>

          <Route
            path="/dashboard/subscriptions"
            element={
              <ProtectedRoute allowedRoles={superAdminOnly}>
                <AdminShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<ManageSubscriptionsPage />} />
          </Route>

          <Route
            path="/dashboard/website-control"
            element={
              <ProtectedRoute allowedRoles={superAdminOnly}>
                <AdminShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<WebsiteControlPage />} />
          </Route>

          <Route
            path="/dashboard/audit-logs"
            element={
              <ProtectedRoute allowedRoles={superAdminOnly}>
                <AdminShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<AuditLogsPage />} />
          </Route>

          <Route
            path="/dashboard/platform-settings"
            element={
              <ProtectedRoute allowedRoles={superAdminOnly}>
                <AdminShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<PlatformSettingsPage />} />
          </Route>

          {/* ────────────────────────────────────────────────────────────────── */}
          {/* Tenant / platform routes — existing Layout shell                  */}
          {/* Preserved for backward compatibility and non-super-admin roles     */}
          {/* ────────────────────────────────────────────────────────────────── */}
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={['super_admin', 'owner', 'project_manager', 'agronomist', 'field_facilitator', 'farmer']}>
              <Layout />
            </ProtectedRoute>
          }>
            {/* Dashboard home - conditional based on role via DashboardWrapper */}
            <Route index element={<DashboardWrapper />} />

            {/* Farmers - project_manager, agronomist, field_facilitator */}
            <Route path="farmers" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist', 'field_facilitator']}>
                <TenantAccessGuard page="farmers" feature="farmers"><FarmersPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Farmer profile full page */}
            <Route path="farmers/:id" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist', 'field_facilitator']}>
                <TenantAccessGuard page="farmers" feature="farmers"><FarmerProfilePage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Cohorts - project_manager, agronomist, field_facilitator */}
            <Route path="cohorts" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist', 'field_facilitator']}>
                <TenantAccessGuard page="cohorts" feature="cohorts"><CohortsPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Compost - project_manager, agronomist */}
            <Route path="compost" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist']}>
                <TenantAccessGuard page="compost" feature="compost"><CompostPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Guidance & Training - project_manager, field_facilitator, agronomist */}
            <Route path="training" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'field_facilitator', 'agronomist']}>
                <TenantAccessGuard page="training" feature="training"><TrainingPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Private guidance library, review queue and cited ask */}
            <Route path="guidance" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist']}>
                <TenantAccessGuard page="guidance" feature="advisory"><GuidancePage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* VSLA - project_manager, field_facilitator */}
            <Route path="vsla" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'field_facilitator']}>
                <TenantAccessGuard page="vsla" feature="vsla"><VSLA /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Inputs & Sales — REMOVED: use marketplace supplier portal instead */}

            {/* Crops Management - project_manager, agronomist */}
            <Route path="crops" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist']}>
                <TenantAccessGuard page="crops" feature="crops"><CropsPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            <Route path="traceability" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist', 'field_facilitator']}>
                <TenantAccessGuard page="traceability" feature="traceability"><TraceabilityPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            <Route path="tree-inspections" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist', 'field_facilitator']}>
                <TenantAccessGuard page="tree-inspections" feature="traceability"><TreeInspectionsPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            <Route path="activities" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist', 'field_facilitator']}>
                <TenantAccessGuard page="activities" feature="activities"><ActivitiesPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Owner and Project Manager get a read-only, KPI-filterable work ledger. */}
            <Route path="owner-work" element={
              <ProtectedRoute allowedRoles={['owner', 'project_manager']}>
                <TenantAccessGuard page="activities" feature="activities"><OwnerWorkPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Morning Rounds - agronomists and managers */}
            <Route path="morning-rounds" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist']}>
                <TenantAccessGuard page="activities" feature="activities"><MorningRoundsPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Field Intelligence - management view */}
            <Route path="field-intelligence" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist']}>
                <TenantAccessGuard page="activities" feature="activities"><FieldIntelligencePage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Field Diary - scouts and managers */}
            <Route path="field-diary" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist', 'field_facilitator']}>
                <TenantAccessGuard page="field-diary" feature="field_diary"><FieldDiaryPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Warehouse - project_manager, agronomist */}
            <Route path="warehouse" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist']}>
                <TenantAccessGuard page="warehouse" feature="warehouse"><WarehouseView /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Maps - project_manager, agronomist */}
            <Route path="maps" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist']}>
                <TenantAccessGuard page="maps" feature="maps"><MapsPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Finance managers see the workspace ledger; field staff see only expenses assigned to them. */}
            <Route path="money" element={
              <ProtectedRoute allowedRoles={['owner', 'project_manager', 'agronomist', 'field_facilitator', 'tenant_admin', 'business_manager', 'finance_admin']}>
                <TenantAccessGuard page="farm-finance" feature="farm_finance"><MoneyPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Analytics - explicit page grant; sections filter themselves */}
            <Route path="analytics" element={
              <ProtectedRoute allowedRoles={['super_admin', 'owner', 'project_manager']}>
                <TenantAccessGuard page="analytics"><AnalyticsPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Users Management - project_manager, super_admin */}
            <Route path="users" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager']}>
                <TenantAccessGuard page="users"><UsersPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Legacy Super Admin route — redirects to new shell */}
            <Route path="super-admin" element={<Navigate to="/dashboard/super-admin" replace />} />

            {/* Legacy company-management — redirects to new shell */}
            <Route path="company-management" element={<Navigate to="/dashboard/company-management" replace />} />

            {/* Legacy subscriptions — redirects to new shell */}
            <Route path="subscriptions" element={<Navigate to="/dashboard/subscriptions" replace />} />

            {/* Audit Logs — redirects to new shell */}
            <Route path="audit-logs" element={<Navigate to="/dashboard/audit-logs" replace />} />

            {/* Donations - super_admin, project_manager */}
            <Route path="donations" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager']}>
                <TenantAccessGuard page="donations" feature="donations"><DonationsPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Funding Readiness - admin roles */}
            <Route path="funding-readiness" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager']}>
                <TenantAccessGuard page="funding-readiness" feature="funding_readiness"><FundingReadinessPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            <Route path="blogs" element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <BlogPage />
              </ProtectedRoute>
            } />

            {/* Legacy website-control — redirects to new shell */}
            <Route path="website-control" element={<Navigate to="/dashboard/website-control" replace />} />

            {/* Managers/owners review reports; field staff can report and triage. */}
            <Route path="issues" element={
              <ProtectedRoute allowedRoles={['owner', 'project_manager', 'agronomist', 'field_facilitator']}>
                <TenantAccessGuard page="issues" feature="issues"><IssueManagementPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Orders - tenant operations */}
            <Route path="orders" element={
              <ProtectedRoute allowedRoles={['super_admin', 'project_manager', 'agronomist', 'field_facilitator']}>
                <TenantAccessGuard page="orders" feature="orders"><OrdersPage /></TenantAccessGuard>
              </ProtectedRoute>
            } />

            {/* Profile - all roles */}
            <Route path="profile" element={<ProfilePage />} />

            {/* Settings - all roles */}
            <Route path="settings" element={<SettingsPage />} />
          </Route>
        
          {/* ────────────────────────────────────────────────────────────────── */}
          {/* B2B Marketplace Routes                                            */}
          {/* ────────────────────────────────────────────────────────────────── */}

          {/* Buyer Portal */}
          <Route path="/marketplace/buyer" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><BuyerOverview /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/catalogue" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><CatalogueBrowser /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/rfqs/new" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><RfqBuilder /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/orders" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><BuyerOrders /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/orders/:id" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><OrderDetail /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/rfqs" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><RfqList /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/quotations" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><BuyerQuotations /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/quotations/:id" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><QuotationDetail /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/rfqs/:id" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><RfqDetail /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/organisation" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><BuyerOrganisation /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/profile" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><BuyerProfile /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/deliveries" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><BuyerDeliveries /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/invoices" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><BuyerInvoices /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/buyer/suppliers" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="buyer"><MarketplaceShell activeTab="buyer"><BuyerSuppliers /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />

          {/* Supplier Portal */}
          <Route path="/marketplace/supplier" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><SupplierOverview /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/supplier/profile" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><SupplierProfilePage /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/supplier/listings" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><SupplierListings /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/supplier/listings/new" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><ListingEditor /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/supplier/listings/:id" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><SupplierListingDetail /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/supplier/listings/:id/edit" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><ListingEditor /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/supplier/rfqs" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><SupplierRfqs /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/supplier/quotations" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><SupplierQuotations /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/supplier/orders" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><SupplierOrders /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/supplier/orders/:id" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><SupplierOrderDetail /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/supplier/fulfilment" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><SupplierFulfilment /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/supplier/inventory" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><SupplierInventory /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />
          <Route path="/marketplace/supplier/settlements" element={<ProtectedRoute><MarketplaceRouteGuard requiredOrganisationType="supplier"><MarketplaceShell activeTab="supplier"><SupplierSettlements /></MarketplaceShell></MarketplaceRouteGuard></ProtectedRoute>} />

          {/* Super Admin Marketplace Control Center */}
          <Route path="/marketplace/admin" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminShell /></ProtectedRoute>}>
              <Route index element={<MarketplaceOverviewPage />} />
              <Route path="suppliers" element={<MarketplaceSuppliersPage />} />
              <Route path="verification" element={<MarketplaceVerificationPage />} />
              <Route path="orders" element={<MarketplaceOrdersPage />} />
              <Route path="finance" element={<MarketplaceFinancePage />} />
              <Route path="settings" element={<MarketplaceSettingsPage />} />
            </Route>

          </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
